import React, { useState, useEffect } from 'react';
import { formatMontant, getDeviseCourante } from '../../utils/format';
import { Plus, Trash2, Search } from 'lucide-react';
import api from '../../services/api';
import { Button, Input, Textarea } from '../../components/common/Input';
import DatePicker from '../../components/common/DatePicker';
import InputMontant from '../../components/common/InputMontant';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';

export default function FormApprovisionnement({ fournisseur, approEdit, onSuccess, onCancel }) {
  const { user } = useAuth();
  // Mode correction : on édite un appro déjà enregistré (approEdit fourni).
  const estEdition = !!approEdit;
  // Un gérant affecté à une boutique ne choisit PAS la boutique : le stock
  // entre forcément dans la sienne (le backend force aussi côté serveur).
  const boutiqueVerrouillee = user?.role === 'gerant_vendeur';
  const [date, setDate] = useState(approEdit?.date ? String(approEdit.date).slice(0, 10) : new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = useState(approEdit?.notes || '');
  const [lignes, setLignes] = useState(() =>
    approEdit
      ? (approEdit.lignes || []).filter(l => l.variante_id).map(l => ({
          variante_id: l.variante_id,
          code_variante: l.code_variante,
          produit_nom: l.produit_nom,
          modele: l.modele,
          taille: l.taille || '',
          couleur: l.couleur,
          quantite: l.quantite,
          prix_achat_unitaire: l.prix_achat_unitaire,
        }))
      : []
  );
  const [search, setSearch] = useState('');
  const [produits, setProduits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]);

  // Boutique de destination de l'approvisionnement (le stock entre ici).
  const [boutiques, setBoutiques] = useState([]);
  const [boutiqueId, setBoutiqueId] = useState('');

  // Conditionnements (cartons/lots) achetables groupés
  const [cartons, setCartons] = useState([]);

  // Charger les produits pour la recherche
  useEffect(() => {
    api.get('/produits', { params: { limit: 200 } })
      .then(r => setProduits(r.data.produits || []))
      .catch(() => {});
    api.get('/cartons').then(r => setCartons(r.data || [])).catch(() => {});
    // Boutiques actives pour le choix de destination
    api.get('/boutiques')
      .then(r => {
        const actives = (r.data || []).filter(b => b.statut === 'actif');
        setBoutiques(actives);
        // Présélection : « Principale » si elle existe, sinon la 1re.
        const principale = actives.find(b => b.nom === 'Principale') || actives[0];
        if (principale) setBoutiqueId(principale.id);
      })
      .catch(() => {});
  }, []);

  // Filtrer en local (articles à l'unité + conditionnements)
  useEffect(() => {
    if (!search.trim()) { setSearchResults([]); return; }
    const q = search.toLowerCase();
    const results = [];
    // Conditionnements (cartons) d'abord
    cartons.forEach(c => {
      if (c.nom?.toLowerCase().includes(q) || c.code?.toLowerCase().includes(q)) {
        results.push({ carton: c });
      }
    });
    produits.forEach(p => {
      (p.variantes || []).filter(v => v.actif).forEach(v => {
        if (
          p.nom.toLowerCase().includes(q) ||
          v.modele?.toLowerCase().includes(q) ||
          v.couleur?.toLowerCase().includes(q) ||
          (q.length >= 4 && v.code_variante?.toLowerCase().includes(q))
        ) {
          results.push({ produit: p, variante: v });
        }
      });
    });
    setSearchResults(results.slice(0, 12));
  }, [search, produits, cartons]);

  const ajouterLigne = (produit, variante) => {
    if (lignes.find(l => l.variante_id === variante.id)) {
      toast('Déjà ajouté — modifiez la quantité', { icon: '⚠️' });
      return;
    }
    setLignes(prev => [{
      variante_id: variante.id,
      code_variante: variante.code_variante,
      produit_nom: produit.nom,
      modele: variante.modele,
      taille: variante.taille_nom || '',
      couleur: variante.couleur,
      quantite: 1,
      // Pré-rempli avec le dernier prix d'achat connu (modifiable).
      prix_achat_unitaire: variante.dernier_prix_achat || 0,
    }, ...prev]);
    setSearch('');
    setSearchResults([]);
  };

  const ajouterCarton = (carton) => {
    if (lignes.find(l => l.carton_id === carton.id)) {
      toast('Conditionnement déjà ajouté', { icon: '⚠️' });
      return;
    }
    const contenu = (carton.composants || [])
      .map(c => `${c.quantite}× ${c.produit_nom}${c.modele ? ` (${c.modele})` : ''}`).join(' + ');
    setLignes(prev => [{
      carton_id: carton.id,
      produit_nom: `${carton.nom}`,
      modele: contenu,
      taille: '', couleur: '',
      quantite: 1,
      prix_achat_unitaire: 0,
    }, ...prev]);
    setSearch('');
    setSearchResults([]);
  };

  const mettreAJour = (i, field, value) => {
    setLignes(prev => prev.map((l, idx) => idx === i ? { ...l, [field]: value } : l));
  };

  const supprimer = (i) => setLignes(prev => prev.filter((_, idx) => idx !== i));

  const total = lignes.reduce((s, l) => s + (parseInt(l.quantite) || 0) * (parseInt(l.prix_achat_unitaire) || 0), 0);

  const handleSubmit = async () => {
    if (lignes.length === 0) return toast.error('Ajoutez au moins un article');
    for (const l of lignes) {
      if (!l.quantite || l.quantite <= 0) return toast.error(`Quantité invalide pour ${l.produit_nom}`);
      if (!l.prix_achat_unitaire || l.prix_achat_unitaire <= 0) return toast.error(`Prix manquant pour ${l.produit_nom}`);
    }
    if (!estEdition && !boutiqueVerrouillee && boutiques.length > 1 && !boutiqueId) {
      return toast.error('Choisissez la boutique qui reçoit la marchandise');
    }
    setLoading(true);
    try {
      const lignesPayload = lignes.map(l => (
        l.carton_id
          ? { carton_id: l.carton_id, quantite: parseInt(l.quantite), prix_achat_unitaire: parseInt(l.prix_achat_unitaire) }
          : { variante_id: l.variante_id, quantite: parseInt(l.quantite), prix_achat_unitaire: parseInt(l.prix_achat_unitaire) }
      ));
      if (estEdition) {
        // Correction d'un appro existant : stock, caisse et coût moyen recalculés serveur.
        await api.put(`/fournisseurs/approvisionnements/${approEdit.id}`, { notes, lignes: lignesPayload });
      } else {
        await api.post(`/fournisseurs/${fournisseur.id}/approvisionnements`, {
          date, notes,
          // Gérant : on n'envoie pas de boutique → le backend force la sienne.
          boutique_id: boutiqueVerrouillee ? undefined : (boutiqueId || undefined),
          lignes: lignesPayload,
        });
      }
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur serveur');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* Bandeau d'info en mode correction */}
      {estEdition && (
        <div className="bg-amber-500/10 border border-amber-500/40 rounded-xl p-3 text-xs text-amber-200">
          ✏️ Correction d'un approvisionnement du <strong>{date}</strong>. La boutique et la date ne changent pas.
          En enregistrant, le <strong>stock</strong>, la <strong>caisse</strong> et le <strong>coût moyen</strong> seront recalculés automatiquement.
        </div>
      )}

      {/* Boutique de destination — visible si plusieurs boutiques.
          Masqué pour un gérant (verrouillé) et en mode correction (boutique figée). */}
      {!estEdition && !boutiqueVerrouillee && boutiques.length > 1 && (
        <div className="bg-yellow-500/10 border border-yellow-500/40 rounded-xl p-3">
          <label className="block text-sm font-semibold text-yellow-400 mb-1.5">
            Boutique qui reçoit la marchandise *
          </label>
          <select
            value={boutiqueId}
            onChange={e => setBoutiqueId(e.target.value)}
            className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-yellow-500"
          >
            <option value="">— Choisir la boutique —</option>
            {boutiques.map(b => (
              <option key={b.id} value={b.id}>{b.nom}{b.ville ? ` — ${b.ville}` : ''}</option>
            ))}
          </select>
          <p className="text-[11px] text-gray-400 mt-1">Le stock entrera dans cette boutique.</p>
        </div>
      )}

      {/* Date et notes */}
      <div className="grid grid-cols-2 gap-4">
        <DatePicker label="Date de livraison" value={date} onChange={e => setDate(e.target.value)} />
        <Textarea label="Notes" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Notes..." rows={1} />
      </div>

      {/* Recherche produit */}
      <div>
        <label className="block text-sm text-gray-400 mb-1.5">Rechercher un produit à ajouter</label>
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Nom, modèle, couleur, code..."
            className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg pl-9 pr-4 py-2.5 text-sm focus:outline-none focus:border-yellow-500"
          />
          {/* Dropdown résultats */}
          {searchResults.length > 0 && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-gray-800 border border-gray-700 rounded-xl shadow-2xl z-20 max-h-64 overflow-y-auto">
              {searchResults.map((r, idx) => r.carton ? (
                <button
                  key={`c-${r.carton.id}`}
                  onClick={() => ajouterCarton(r.carton)}
                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-700 transition-colors text-left border-l-2 border-yellow-500/60"
                >
                  <div className="min-w-0">
                    <p className="text-white text-sm font-medium">{r.carton.nom} <span className="text-yellow-400/80 text-[11px]">conditionnement</span></p>
                    <p className="text-gray-400 text-xs truncate">
                      {(r.carton.composants || []).map(c => `${c.quantite}× ${c.produit_nom}`).join(' + ')}
                    </p>
                  </div>
                </button>
              ) : (
                <button
                  key={`v-${r.variante.id}`}
                  onClick={() => ajouterLigne(r.produit, r.variante)}
                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-700 transition-colors text-left"
                >
                  <div>
                    <p className="text-white text-sm font-medium">{r.produit.nom}</p>
                    <p className="text-gray-400 text-xs">{r.variante.modele} {r.variante.couleur} · {r.variante.code_variante}</p>
                  </div>
                  <span className="text-yellow-400 text-xs ml-3 flex-shrink-0">
                    Stock: {r.variante.stock_disponible || 0}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Lignes d'approvisionnement */}
      {lignes.length > 0 ? (
        <div className="space-y-2">
          <div className="hidden sm:grid sm:grid-cols-12 gap-2 px-2 text-xs text-gray-500 uppercase">
            <div className="col-span-5">Produit</div>
            <div className="col-span-2 text-center">Qté</div>
            <div className="col-span-3 text-center">Prix achat unitaire</div>
            <div className="col-span-1 text-right">Sous-total</div>
            <div className="col-span-1"></div>
          </div>

          {lignes.map((l, i) => (
            // Key stable = variante_id (unique, garantit que React ne réutilise
            // pas par erreur les inputs d'une ligne supprimée). i reste utilisé
            // par les callbacks pour cibler la ligne dans setLignes.
            <div key={l.carton_id || l.variante_id} className="flex flex-col gap-2 sm:grid sm:grid-cols-12 sm:items-center bg-gray-800 rounded-xl px-3 py-2.5">
              {/* Produit */}
              <div className="col-span-5 min-w-0">
                <p className="text-white text-sm font-medium truncate">{l.produit_nom}</p>
                <p className="text-gray-500 text-xs truncate">
                  {l.carton_id ? l.modele : `${l.modele || ''} ${l.couleur || ''}`.trim()}
                </p>
                {l.carton_id && <p className="text-yellow-400/70 text-[10px]">Qté = nombre de conditionnements · Prix = prix d'un conditionnement</p>}
              </div>
              {/* Quantité */}
              <div className="col-span-2">
                <input
                  type="number" min="1"
                  value={l.quantite || ''}
                  onChange={e => mettreAJour(i, 'quantite', e.target.value === '' ? 0 : parseInt(e.target.value) || 0)}
                  placeholder="0"
                  className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-2 py-1.5 text-sm text-center focus:outline-none focus:border-yellow-500"
                />
              </div>
              {/* Prix achat */}
              <div className="col-span-3">
                <InputMontant
                  value={l.prix_achat_unitaire}
                  onChange={v => mettreAJour(i, 'prix_achat_unitaire', v)}
                  placeholder={`0 ${getDeviseCourante()}`}
                  className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-2 py-1.5 text-sm text-center focus:outline-none focus:border-yellow-500"
                />
              </div>
              {/* Sous-total */}
              <div className="col-span-1 text-right text-yellow-400 text-xs font-medium">
                {formatMontant(parseInt(l.quantite || 0) * parseInt(l.prix_achat_unitaire || 0))}
              </div>
              {/* Supprimer */}
              <div className="col-span-1 flex justify-center">
                <button onClick={() => supprimer(i)} className="p-1.5 text-red-400 hover:bg-red-900/30 rounded-lg transition-colors">
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
          ))}

          {/* Total */}
          <div className="flex items-center justify-between bg-yellow-600/10 border border-yellow-600/30 rounded-xl px-4 py-3 mt-2">
            <span className="text-gray-300 text-sm font-medium">Coût total de l'approvisionnement</span>
            <span className="text-yellow-400 font-bold text-lg">{formatMontant(total)}</span>
          </div>
        </div>
      ) : (
        <div className="border-2 border-dashed border-gray-700 rounded-xl p-8 text-center">
          <p className="text-gray-500 text-sm">Recherchez des produits ci-dessus pour les ajouter</p>
        </div>
      )}

      {/* Actions */}
      <div className="flex space-x-3 pt-2">
        <Button type="button" variant="secondary" onClick={onCancel} className="flex-1">{' '}Annuler</Button>
        <Button loading={loading} onClick={handleSubmit} className="flex-1" disabled={lignes.length === 0}>
          Enregistrer ({lignes.length} article{lignes.length > 1 ? 's' : ''})
        </Button>
      </div>
    </div>
  );
}
